import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axiosInstance from '../api/axios';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';
import { formatEUR } from '../utils/format';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Ausstehend', variant: 'secondary' },
      confirmed: { label: 'Bestätigt', variant: 'default' },
      processing: { label: 'In Bearbeitung', variant: 'default' },
      shipped: { label: 'Versandt', variant: 'default' },
      delivered: { label: 'Geliefert', variant: 'default' },
      cancelled: { label: 'Storniert', variant: 'destructive' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#E8E0D5' }}>
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="orders-title">Meine Bestellungen</h1>
        
        {orders.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Keine Bestellungen</h2>
              <p className="text-gray-600 mb-6">Sie haben noch keine Bestellungen aufgegeben</p>
              <Link
                to="/products"
                className="inline-block bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800"
              >
                Jetzt einkaufen
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((order) => (
              <Card key={order.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">Bestellung #{order.id.slice(0, 8)}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        Bestellt am {formatDate(order.created_at)}
                      </p>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Order Items */}
                    <div className="space-y-2">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            {item.quantity}x {item.product_name}
                          </span>
                          <span className="font-medium">
                            {formatEUR((item.price * item.quantity))}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Total */}
                    <div className="border-t pt-3 flex justify-between">
                      <span className="font-semibold">Gesamt</span>
                      <span className="font-bold text-lg">{formatEUR(order.total_amount)}</span>
                    </div>
                    
                    {/* Shipping Address */}
                    <div className="border-t pt-3">
                      <p className="text-sm font-medium text-gray-900 mb-1">Lieferadresse</p>
                      <p className="text-sm text-gray-600">
                        {order.shipping_address.first_name} {order.shipping_address.last_name}<br />
                        {order.shipping_address.street} {order.shipping_address.house_number}<br />
                        {order.shipping_address.postal_code} {order.shipping_address.city}
                      </p>
                    </div>
                    
                    {/* View Details Link */}
                    <Link
                      to={`/orders/${order.id}`}
                      className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Details ansehen <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;